import {debounce, isEqual} from "lodash";
import _root from "window-or-global";

import {decide} from "./decide";

import type {Flags} from "./constants";

type FlagDefinition = (typeof Flags)[keyof typeof Flags];

const root = _root as typeof _root & {
    optimizelyDecisionSuscribers: Set<() => void>;
};

root.optimizelyDecisionSuscribers ??= new Set<() => void>();

export const notifyDecisionSubscribers = debounce(() => {
    root.optimizelyDecisionSuscribers.forEach((callback) => callback());
}, 0);

/**
 * Subscribes to initialization, attribute changes, and config updates.
 * Can be called before Optimizely exists; invokes the callback immediately and subsequently on decision changes.
 * Notifications follow decision cache invalidation and do not guarantee SDK readiness.
 */
export const subscribeToDecision = <T extends FlagDefinition>(
    flag: T,
    callback: (result: ReturnType<typeof decide<T>>) => void,
): (() => void) => {
    let prevResult = decide(flag);
    callback(prevResult);

    const subscriber = () => {
        decide.cache?.clear?.();
        const newResult = decide(flag);
        if (!isEqual(newResult, prevResult)) {
            callback(newResult);
            prevResult = newResult;
        }
    };
    root.optimizelyDecisionSuscribers.add(subscriber);
    return () => {
        root.optimizelyDecisionSuscribers.delete(subscriber);
    };
};
